/**
 * SparkLoader — lazy loader for @sparkjsdev/spark v2.1.0.
 *
 * The spark bundle is copied into `build/potree/lazylibs/gs-spark/` by the gulp
 * `gs-spark` task, alongside a private `three.module.js` copy so that spark's
 * bare specifier imports (`from "three"`, `from "three/addons/..."`) resolve to
 * self-contained relative paths (see gulpfile.js).
 *
 * This module does not eagerly resolve spark; the first call to `load()` triggers
 * a dynamic `import(url)` so consumer HTML pages that never use 3DGS never pay the
 * download cost.
 *
 * NOTE: `Potree.scriptPath` is referenced as a runtime global (matching the pattern
 * used elsewhere in this codebase, e.g. `src/loader/BinaryLoader.js:63`,
 * `src/loader/GeoPackageLoader.js:44`). Rollup wraps this file into the UMD bundle
 * where `Potree` resolves to the module's own exports namespace at runtime.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * Cross-realm THREE.ShaderChunk sync
 * ─────────────────────────────────────────────────────────────────────────
 * Spark v2.x imports `three` and, at module top level, mutates
 * `THREE.ShaderChunk.splatDefines = ...`. Because spark's `three` resolves to
 * `build/potree/lazylibs/gs-spark/three.module.js` (its own private copy), that
 * mutation lands in a DIFFERENT `THREE` realm than the one Potree's WebGLRenderer
 * bundled inside `build/potree/potree.js` uses. Result: when Potree's renderer
 * compiles a SplatMesh material, `#include <splatDefines>` cannot be resolved.
 *
 * Workaround: after spark loads, also `import()` spark's THREE module (same URL,
 * cached by the browser) so we can read its `ShaderChunk.splatDefines`, then
 * write the same string into Potree bundle's `ShaderChunk.splatDefines` (which
 * is reachable via `Potree.THREE.ShaderChunk` — exported from `src/Potree.js`).
 *
 * Any additional chunks spark introduces in future versions can be added to
 * `SYNCED_CHUNKS` below.
 */

/** ShaderChunk keys registered by spark that must be mirrored into Potree's THREE realm. */
const SYNCED_CHUNKS = ["splatDefines"];

let _sparkPromise = null;

/**
 * Resolve and cache the spark module. Returns the whole module namespace, which
 * exports `SplatMesh`, `SparkRenderer`, `SparkXr`, `PagedSplats`, `SplatEdit`,
 * `SplatSkinning`, ... — see `libs/gs-spark/types/index.d.ts` for the full surface.
 *
 * On first resolution this also syncs spark-registered ShaderChunk entries into
 * Potree bundle's THREE realm so cross-realm shader compilation works.
 *
 * @returns {Promise<any>} the sparkjs module namespace.
 */
export function load() {
	if (!_sparkPromise) {
		const base = `${Potree.scriptPath}/lazylibs/gs-spark`;
		const sparkUrl = `${base}/spark.module.js`;
		const threeUrl = `${base}/three.module.js`;
		// Note: the URLs are composed at runtime so rollup does NOT statically
		// analyse them as dependencies, and spark stays out of potree.js.
		// Importing spark's THREE (same URL as spark's internal `from "three"`)
		// is free — the browser cache hits so only one network request happens.
		_sparkPromise = Promise.all([
			import(/* @vite-ignore */ /* webpackIgnore: true */ sparkUrl),
			import(/* @vite-ignore */ /* webpackIgnore: true */ threeUrl),
		]).then(([spark, sparkThree]) => {
			syncShaderChunks(sparkThree);
			return spark;
		});
	}
	return _sparkPromise;
}

/**
 * Copy spark-registered chunks (populated by spark's top-level side effects)
 * from spark's THREE realm into Potree bundle's THREE realm. Idempotent.
 */
function syncShaderChunks(sparkThree) {
	// Potree.THREE is the *same object* used by Potree's internal WebGLRenderer
	// (rollup dedupes the module inside the bundle), so mutating its ShaderChunk
	// map is observed by the renderer's `resolveIncludes()`.
	const potreeShaderChunk = Potree?.THREE?.ShaderChunk;
	const sparkShaderChunk  = sparkThree?.ShaderChunk;
	if (!potreeShaderChunk || !sparkShaderChunk) return;

	for (const key of SYNCED_CHUNKS) {
		if (sparkShaderChunk[key] && !potreeShaderChunk[key]) {
			potreeShaderChunk[key] = sparkShaderChunk[key];
		}
	}
}

/** Whether spark has been requested at least once. */
export function isLoaded() {
	return _sparkPromise !== null;
}
